import { useCallback, useMemo, useState } from 'react';

import { useTranslate } from '@dreryk/react-common';

import { TagActions } from '~libs.desktop.shared.document-template';

import { translationKeys } from '../../translations';

import { VisitDocumentsDiagnosesModal } from './VisitDocumentsDiagnosesModal';

type PendingAction = { resolve: (value: string | undefined) => void };

type VisitDocumentsAddDiagnosesModalOpenerRenderProps = {
   tagActions: TagActions;
};

type VisitDocumentsAddDiagnosesModalOpenerProps = Readonly<{
   children: (renderProps: VisitDocumentsAddDiagnosesModalOpenerRenderProps) => JSX.Element;
}>;

export const VisitDocumentsAddDiagnosesModalOpener = (
   props: VisitDocumentsAddDiagnosesModalOpenerProps
): JSX.Element => {
   const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
   const translate = useTranslate();

   const handleOpen = useCallback(
      async (): Promise<string | undefined> =>
         new Promise<string | undefined>((resolve) => {
            setPendingAction({ resolve: resolve });
         }),
      []
   );

   const tagActions = useMemo<TagActions>(
      () => ({
         VISIT_DIAGNOSES: {
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
               <path fill-rule="evenodd" clip-rule="evenodd" d="M12 22C6.48 22 2 17.52 2 12C2 6.48 6.48 2 12
               2C17.52 2 22 6.48 22 12C22 17.52 17.52 22 12 22ZM13 17V13H17V11H13V7H11V11H7V13H11V17H13Z"
               fill="currentColor"/></svg>`,
            label: translate(translationKeys.addDiagnosis),
            style: `position: absolute; bottom: -15px; left: 0; cursor: pointer; display: flex;
               align-items: center; gap: 4px; padding: 0; border: none; background: none;
               color: #589AE9; font-size: 12px;`,
            handler: handleOpen,
         },
         VISIT_MAIN_DIAGNOSIS: {
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
               <path fill-rule="evenodd" clip-rule="evenodd" d="M12 22C6.48 22 2 17.52 2 12C2 6.48 6.48 2 12
               2C17.52 2 22 6.48 22 12C22 17.52 17.52 22 12 22ZM13 17V13H17V11H13V7H11V11H7V13H11V17H13Z"
               fill="currentColor"/></svg>`,
            label: translate(translationKeys.addDiagnosis),
            style: `position: absolute; bottom: -15px; left: 0; cursor: pointer; display: flex;
               align-items: center; gap: 4px; padding: 0; border: none; background: none;
               color: #589AE9; font-size: 12px;`,
            handler: handleOpen,
         },
         VISIT_ADDITIONAL_DIAGNOSES: {
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
               <path fill-rule="evenodd" clip-rule="evenodd" d="M12 22C6.48 22 2 17.52 2 12C2 6.48 6.48 2 12
               2C17.52 2 22 6.48 22 12C22 17.52 17.52 22 12 22ZM13 17V13H17V11H13V7H11V11H7V13H11V17H13Z"
               fill="currentColor"/></svg>`,
            label: translate(translationKeys.addDiagnosis),
            style: `position: absolute; bottom: -15px; left: 0; cursor: pointer; display: flex;
               align-items: center; gap: 4px; padding: 0; border: none; background: none;
               color: #589AE9; font-size: 12px;`,
            handler: handleOpen,
         },
      }),
      [handleOpen, translate]
   );

   const handleConfirm = useCallback(
      (value: string): void => {
         pendingAction?.resolve(value);
         setPendingAction(null);
      },
      [pendingAction]
   );

   const handleClose = useCallback((): void => {
      pendingAction?.resolve(undefined);
      setPendingAction(null);
   }, [pendingAction]);

   return (
      <>
         {props.children({ tagActions: tagActions })}
         {pendingAction ? <VisitDocumentsDiagnosesModal onConfirm={handleConfirm} onClose={handleClose} /> : null}
      </>
   );
};
